import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { AssistantNoteListApi } from './AssistantNoteApiProviderTypes';

interface AssistantNoteApiContextType {
  getAssistantNotes: (
    assistantKey: string,
    skip?: number,
    limit?: number,
  ) => Promise<AssistantNoteListApi>;
  deleteAssistantNote: (noteId: string) => Promise<void>;
  exportAssistantNotes: (assistantKey: string, noteIds?: string[]) => Promise<void>;
}

const AssistantNoteApiContext = createContext<AssistantNoteApiContextType | null>(null);

export function AssistantNoteApiProvider({ children }: { children: React.ReactNode }) {
  const { get, del } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getAssistantNotes = useCallback(
    async (
      assistantKey: string,
      skip: number = 0,
      limit: number = 10,
    ): Promise<AssistantNoteListApi> => {
      try {
        const response = await get(
          `/v1/assistant_notes?assistant_key=${assistantKey}&skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching assistant notes:', error);
        throw error;
      }
    },
    [get],
  );

  const deleteAssistantNote = useCallback(
    async (noteId: string): Promise<void> => {
      try {
        await del(`/v1/assistant_notes/${noteId}`);
      } catch (error) {
        console.error('Error deleting assistant note:', error);
        throw error;
      }
    },
    [del],
  );

  const exportAssistantNotes = useCallback(
    async (assistantKey: string, noteIds?: string[]): Promise<void> => {
      try {
        const search = new URLSearchParams();
        search.append('assistant_key', assistantKey);
        (noteIds ?? []).forEach((noteId) => search.append('note_keys', noteId));

        const response = await get(`/v1/assistant_notes/export?${search.toString()}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'agent_notes.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting assistant notes:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <AssistantNoteApiContext.Provider
      value={{
        getAssistantNotes,
        deleteAssistantNote,
        exportAssistantNotes,
      }}
    >
      {children}
    </AssistantNoteApiContext.Provider>
  );
}

export function useAssistantNoteApi(): AssistantNoteApiContextType {
  const context = useContext(AssistantNoteApiContext);
  if (!context) {
    throw new Error('useAssistantNoteApi must be used within an AssistantNoteApiProvider');
  }
  return context;
}
